import React, { useEffect, useState } from "react";
import { Gift, Copy, Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/apiClient";

export default function Referral() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/wallet/referral").then((r) => setD(r.data)).catch(() => toast.error("Could not load referral")); }, []);
  if (!d) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>;
  // Always build the shareable link from the stable public domain (not window.location.origin,
  // which changes inside the native/Capacitor app or preview and would break old links).
  const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");
  const link = `${base}${d.link_path}`;
  const copy = (t, label) => { navigator.clipboard.writeText(t); toast.success(`${label} copied`); };
  const share = async () => {
    try { await navigator.share({ title: "Paras CashBack", text: `Join Paras CashBack as a merchant using my code ${d.code}`, url: link }); }
    catch { copy(link, "Link"); }
  };

  return (
    <div className="space-y-6" data-testid="customer-referral">
      <div className="flex items-center gap-2"><Gift className="h-5 w-5 text-emerald-400" /><h1 className="font-heading text-2xl font-bold">Refer a Merchant &amp; Earn</h1></div>
      <p className="text-sm text-slate-400">Bring a new merchant to Paras CashBack. Get ₹125 when they first subscribe, and ₹50 on every renewal — credited straight to your wallet.</p>

      <div className="ledger-card rounded-2xl p-6">
        <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Your referral code</p>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <span data-testid="referral-code" className="font-heading text-3xl font-extrabold tracking-wider text-emerald-400">{d.code}</span>
          <button data-testid="referral-copy-code" onClick={() => copy(d.code, "Code")} className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1.5 text-xs hover:border-emerald-500/50"><Copy className="h-3.5 w-3.5" /> Copy code</button>
          <button data-testid="referral-copy-link" onClick={() => copy(link, "Link")} className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1.5 text-xs hover:border-emerald-500/50"><Copy className="h-3.5 w-3.5" /> Copy link</button>
          <button data-testid="referral-share" onClick={share} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-slate-950 hover:bg-emerald-400"><Share2 className="h-3.5 w-3.5" /> Share</button>
        </div>
        <p className="mt-3 break-all font-mono text-xs text-slate-500">{link}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <Stat label="Merchants referred" value={d.stats.referred} />
        <Stat label="Successful" value={d.stats.successful} />
        <Stat label="First rewards" value={`${d.stats.first_count} · ${d.stats.first_display}`} />
        <Stat label="Renewal rewards" value={`${d.stats.renewal_count} · ${d.stats.renewal_display}`} />
        <Stat label="Total earned" value={d.stats.total_display} accent />
      </div>

      <div className="ledger-card overflow-hidden rounded-2xl">
        <p className="p-4 font-mono text-[11px] uppercase tracking-widest text-slate-500">Referral history</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="referral-history-table">
            <thead className="bg-slate-900/50 font-mono text-xs uppercase tracking-wider text-slate-500"><tr><th className="p-4 text-left">Merchant</th><th className="p-4 text-left">Date</th><th className="p-4 text-left">Status</th><th className="p-4 text-right">First</th><th className="p-4 text-right">Renewals</th><th className="p-4 text-right">Total</th></tr></thead>
            <tbody>
              {d.history.map((h, i) => (
                <tr key={i} className="border-t border-slate-800/60">
                  <td className="p-4">{h.business_name}</td>
                  <td className="p-4 text-slate-400">{h.created_at ? new Date(h.created_at).toLocaleDateString("en-IN") : "—"}</td>
                  <td className="p-4 font-mono text-xs text-emerald-400">{h.status}{h.fraudStatus && h.fraudStatus !== "CLEAR" ? ` · ${h.fraudStatus}` : ""}</td>
                  <td className="p-4 text-right font-mono">{h.first_reward_display}</td>
                  <td className="p-4 text-right font-mono text-slate-400">{h.renewal_count} · {h.renewal_display}</td>
                  <td className="p-4 text-right font-mono text-emerald-400">{h.total_display}</td>
                </tr>
              ))}
              {d.history.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">No referrals yet. Share your code to start earning!</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return <div className="ledger-card rounded-2xl p-4" data-testid={`referral-stat-${label.replace(/\s+/g, "-").toLowerCase()}`}><p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p><p className={`mt-1 font-heading text-lg font-bold ${accent ? "text-emerald-400" : "text-slate-100"}`}>{value}</p></div>;
}
